import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Spin } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import api from "../../../../services/api";
import { PROJECT_MEMBER_ROLES } from "../../constants/projects.constants";
import { errMsg } from "../../helpers/errMsg.helper";
import FieldLabel from "../../components/FieldLabel";
import type { ProjectMemberRole } from "../../../../core/data/redux/projects/types";

interface AddMemberModalProps {
  open: boolean;
  excludedUserIds: number[];
  onSubmit: (p: {
    userId: number;
    role: ProjectMemberRole;
    userSnapshot: Record<string, any>;
  }) => Promise<void>;
  onCancel: () => void;
}

interface EmployeeOption {
  value: number;
  label: string;
  email: string;
  uid: string | null;
  img: string | null;
}

const fetchEmployees = async (): Promise<EmployeeOption[]> => {
  const signature = localStorage.getItem("signature");
  const response = await api.post("userService/getActiveEmployeesListData", { signature });
  const payload = response.data?.data?.data || response.data?.data || {};
  const list = Array.isArray(payload) ? payload : payload.allUsers || [];
  return list.map((emp: any) => {
    const id = emp.id ?? emp.user_id ?? emp.key;
    const name =
      emp.Name ||
      `${emp.Fname || emp.fname || ""} ${emp.Lname || emp.lname || ""}`.trim() ||
      emp.Email ||
      emp.email ||
      `User #${id}`;
    const email = emp.Email || emp.email || "";
    return {
      value: id,
      label: name,
      email,
      uid: emp.UID || emp.uid || emp.EmpId || null,
      img: emp.Image || emp.profile_pic || null,
    };
  });
};

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  open,
  excludedUserIds,
  onSubmit,
  onCancel,
}) => {
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [role, setRole] = useState<ProjectMemberRole>("MEMBER");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUserId(null);
    setRole("MEMBER");
    setError(null);
    setLoading(true);
    fetchEmployees()
      .then((list) => setEmployees(list))
      .catch((e: any) => setError(errMsg(e, "Failed to load employees")))
      .finally(() => setLoading(false));
  }, [open]);

  const options = useMemo(() => {
    const excluded = new Set(excludedUserIds);
    return employees
      .filter((e) => !excluded.has(e.value))
      .map((e) => ({
        value: e.value,
        label: e.email ? `${e.label} — ${e.email}` : e.label,
      }));
  }, [employees, excludedUserIds]);

  const handleSubmit = async () => {
    if (userId == null) { setError("Select a user to add"); return; }
    const employee = employees.find((e) => e.value === userId);
    if (!employee) { setError("Selected user not found"); return; }
    try {
      setSubmitting(true);
      setError(null);
      await onSubmit({
        userId,
        role,
        userSnapshot: {
          id: employee.value,
          name: employee.label,
          email: employee.email,
          uid: employee.uid,
          img: employee.img,
        },
      });
    } catch (e: any) {
      setError(errMsg(e, "Failed to add member"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: { padding: 0, borderRadius: 12, overflow: "hidden" },
        header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
        body: { padding: "16px 24px 20px" },
        footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "#3b82f618",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3b82f6",
              fontSize: 15,
            }}
          >
            <UserAddOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>Add Member</div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", fontWeight: 400 }}>
              Invite a team member to this project
            </div>
          </div>
        </div>
      }
      footer={
        <div
          style={{
            padding: "14px 28px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            className="btn btn-light border px-4"
            style={{ borderRadius: 8 }}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn px-4"
            style={{ borderRadius: 8, background: "#10b981", color: "#fff", fontWeight: 600 }}
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting ? (
              <><span className="spinner-border spinner-border-sm me-2" />Adding...</>
            ) : (
              <><UserAddOutlined className="me-2" />Add Member</>
            )}
          </button>
        </div>
      }
    >
      <div className="d-flex flex-column gap-3">
        {error && (
          <div
            style={{
              padding: "10px 14px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 8,
              color: "#dc2626",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <i className="ti ti-alert-triangle" style={{ fontSize: 16 }} />
            {error}
          </div>
        )}

        <div>
          <FieldLabel required>User</FieldLabel>
          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted small">
              <Spin size="small" /> Loading employees…
            </div>
          ) : (
            <Select
              showSearch
              size="large"
              style={{ width: "100%" }}
              placeholder="Search by name or email"
              options={options}
              value={userId ?? undefined}
              onChange={(v) => setUserId(v ?? null)}
              filterOption={(input, option) =>
                String(option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          )}
        </div>

        <div>
          <FieldLabel>Role</FieldLabel>
          <Select
            size="large"
            style={{ width: "100%" }}
            value={role}
            onChange={(v) => setRole(v as ProjectMemberRole)}
            options={PROJECT_MEMBER_ROLES.map((r) => ({
              value: r.value,
              label: `${r.label} — ${r.description}`,
            }))}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddMemberModal;